import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autosuggest, {
  ChangeEvent,
  GetSuggestionValue,
  OnSuggestionHighlighted,
  OnSuggestionsClearRequested,
  RenderSuggestionParams,
  RenderSuggestionsContainer,
  ShouldRenderSuggestions,
  SuggestionSelectedEventData,
  SuggestionsFetchRequested,
} from 'react-autosuggest';
import classNames from 'classnames';
import _ from 'lodash';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons/faSearch';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { LoadingView } from '../../../common/views/LoadingView';
import { SearchDatabaseResult, SearchDatabaseResultItem, MeiliIndex, SearchItem } from './types';
import { doMeiliSearch } from './searchService';
import { getResultTitle } from './consts';
import { getMapperFromMeiliResponse } from './mappers';
import { TypedMemo } from '../../../common/types';
import { SMLImage } from '../SMLImage';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './SearchField.module.scss';

export type SearchFieldOnResultsPicked = (data: SuggestionSelectedEventData<SearchDatabaseResultItem>) => void;

export type SearchFieldProps = {
  onResultPicked: SearchFieldOnResultsPicked;
  isSimple?: boolean;
  /** Render results in the page flow instead of a floating dropdown — for
   *  hosts whose overflow would clip an absolutely positioned panel (modals). */
  inlineSuggestions?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  queryTypes: MeiliIndex[];
  onBlur?: () => void;
  onFocus?: () => void;
  onSearchTyped?: (text: string) => void;
  itemForRemoval?: {
    item: SearchDatabaseResultItem;
    onRemove: () => void;
    isLoading: boolean;
  };
  forceShowBackdrop?: boolean;
  isMasterSearch?: boolean;
  initialValue?: string;
};

const SearchFieldComponent: React.FC<SearchFieldProps> = ({
  isSimple,
  inlineSuggestions,
  onResultPicked,
  queryTypes,
  placeholder,
  isDisabled,
  isLoading,
  autoFocus,
  onBlur,
  onFocus,
  itemForRemoval,
  forceShowBackdrop,
  isMasterSearch,
  initialValue,
  onSearchTyped,
}) => {
  const intl = useIntl();
  const [isLoadingState, setIsLoadingState] = useState(isLoading);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(initialValue ?? '');
  const [suggestions, setSuggestions] = useState<SearchDatabaseResult[]>([]);
  const [, _setHighlightedSuggestion] = useState<SearchDatabaseResult | null>(null);
  const ref = useRef<Autosuggest<SearchDatabaseResultItem, SearchDatabaseResult>>(null);

  const defaultPlaceholder = intl.formatMessage({
    id: 'app.search.placeholder',
    defaultMessage: 'Search...',
  });

  useEffect(() => {
    if (autoFocus !== true) {
      return;
    }
    ref.current?.input?.focus();
  }, [autoFocus]);

  const onChange = useCallback(
    (event: React.FormEvent<HTMLElement>, params: ChangeEvent) => {
      if (params.method !== 'type') {
        return;
      }
      setInputValue(params.newValue);
      onSearchTyped?.(params.newValue);
    },
    [onSearchTyped]
  );

  const onFocusInput = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      setTimeout(() => {
        event.target.select();
      }, 10);
      onFocus?.();
    },
    [onFocus]
  );

  const onBlurInput = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  const doSearch = useMemo(
    () =>
      _.debounce((value: string) => {
        // Require at least 2 characters
        if (value.trim().length < 2) {
          setSuggestions([]);
          setIsLoadingState(false);
          return;
        }

        const searchFunc = async () => {
          const searchResults = await doMeiliSearch(value, queryTypes);
          if (searchResults === null) {
            return;
          }
          setSuggestions(searchResults.filter((s) => s.items.length !== 0));
        };

        setIsLoadingState(true);
        searchFunc()
          .catch(console.error)
          .finally(() => {
            setIsLoadingState(false);
          });
      }, 300),
    [queryTypes]
  );

  const onSuggestionSelected = useCallback(
    (event: React.FormEvent<any>, data: SuggestionSelectedEventData<SearchDatabaseResultItem>) => {
      onResultPicked(data);
      if (isMasterSearch !== true) {
        setInputValue('');
      }
      setTimeout(() => {
        ref.current?.input?.blur();
      }, 10);
    },
    [isMasterSearch, onResultPicked]
  );

  const onSuggestionHighlighted: OnSuggestionHighlighted = useCallback(({ suggestion }) => {
    _setHighlightedSuggestion(suggestion);
  }, []);

  const onSuggestionsFetchRequested: SuggestionsFetchRequested = useCallback(
    (request) => {
      doSearch(request.value);
    },
    [doSearch]
  );

  const onSuggestionsClearRequested: OnSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, []);

  const shouldRenderSuggestions: ShouldRenderSuggestions = useCallback((value) => value.trim().length >= 3, []);

  const getSectionSuggestions = useCallback((section: SearchDatabaseResult) => section.items, []);

  const getSuggestionValue: GetSuggestionValue<SearchDatabaseResultItem> = useCallback(
    (suggestion) => getMapperFromMeiliResponse(suggestion.type)(suggestion.item as any).title,
    []
  );

  const renderSuggestion: (
    suggestion: SearchDatabaseResultItem,
    params: RenderSuggestionParams,
    extraParams?: {
      onRemoveClick: NonNullable<SearchFieldProps['itemForRemoval']>['onRemove'];
      onRemoveIsLoading: NonNullable<SearchFieldProps['itemForRemoval']>['isLoading'];
    }
  ) => React.ReactNode = useCallback(
    (suggestion, { isHighlighted }, extraParams) => {
      const searchItem: SearchItem = getMapperFromMeiliResponse(suggestion.type)(suggestion.item as any);

      return (
        <div
          key={searchItem.id}
          className={classNames('d-flex align-items-center', styles.suggestion, {
            [styles.isHighlighted as string]: isHighlighted,
            [styles.isExtra as string]: extraParams,
          })}
          onClick={extraParams?.onRemoveClick}
          onKeyUp={extraParams?.onRemoveClick}
          role={extraParams !== undefined ? 'button' : undefined}
          tabIndex={extraParams !== undefined ? 0 : undefined}
        >
          <div
            className={classNames(
              'flex-shrink-0 border rounded-2 overflow-hidden bg-white',
              styles.suggestionImageContainer,
              {
                'p-1': searchItem.isImagePadding === true && searchItem.image !== undefined,
                'rounded-circle': searchItem.isImageRound === true,
              }
            )}
          >
            {searchItem.image !== undefined ? (
              <SMLImage
                src={searchItem.image}
                alt={`${searchItem.title} - ${searchItem.subtitle ?? ''}`}
                width={40}
                makeSquareFit={searchItem.isImageRound === true}
                className={classNames('img-fluid', styles.suggestionImage, {
                  [styles.suggestionImageRounded as string]: searchItem.isImageRound === true,
                })}
              />
            ) : (
              <i className={`${searchItem.placeholderIcon} fa-lg text-muted`} />
            )}
          </div>
          <div className={classNames(styles.suggestionTextContainer, 'text-truncate')}>
            <div className={classNames(styles.suggestionTextTitle, 'd-inline-block')}>{searchItem.title}</div>
            {searchItem.subtitle && (
              <div className={classNames(styles.suggestionTextSubTitle, 'd-inline-block')}>{searchItem.subtitle}</div>
            )}
          </div>
          {extraParams?.onRemoveClick && (
            <div className="ms-3 flex-grow-1 text-end">
              <Button color="secondary" disabled={extraParams?.onRemoveIsLoading}>
                {extraParams?.onRemoveIsLoading ? (
                  <LoadingView />
                ) : (
                  intl.formatMessage({
                    id: 'app.search.remove',
                    defaultMessage: 'Remove',
                  })
                )}
              </Button>
            </div>
          )}
        </div>
      );
    },
    [intl]
  );

  const renderSectionTitle = useCallback(
    (section: SearchDatabaseResult) =>
      queryTypes.length > 1 === true && (
        <div className={styles.sectionTitle}>
          <i className="fa-regular fa-dash" /> {getResultTitle(section.type)}
        </div>
      ),
    [queryTypes.length]
  );

  const renderInputComponent: (inputProps: any) => React.ReactNode = ({ key, value, ...inputProps }) => (
    <div className={classNames(styles.inputContainer, { [styles.inputContainerFocused as string]: isFocused })}>
      <div className={styles.inputIcon}>
        {isLoadingState === true || isLoading === true ? <LoadingView /> : <FontAwesomeIcon icon={faSearch} />}
      </div>
      {isMasterSearch === true && isFocused === true && (
        <div className={classNames(styles.pressEnter)}>
          {intl.formatMessage({
            id: 'app.search.pressEnter',
            defaultMessage: 'Press Enter for more',
          })}
        </div>
      )}
      <input
        key={key}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
        value={isFocused ? value : ''}
        className={classNames(
          inputProps.className,
          styles.input,
          {
            [styles.inputFocused as string]: isFocused,
          },
          'form-control'
        )}
      />
    </div>
  );

  const renderSuggestionsContainer: RenderSuggestionsContainer = useCallback(
    ({ containerProps: { className, key, ...containerProps }, children }) => (
      <div
        className={classNames(className, styles.container, {
          [styles.isSimpleContainer as string]: isSimple,
          [styles.isInlineContainer as string]: inlineSuggestions,
        })}
        key={key}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...containerProps}
      >
        {itemForRemoval !== undefined && (
          <div className={styles.custom}>
            {renderSuggestion(
              itemForRemoval.item,
              { query: '', isHighlighted: false },
              { onRemoveClick: itemForRemoval.onRemove, onRemoveIsLoading: itemForRemoval.isLoading }
            )}
          </div>
        )}
        {children}
      </div>
    ),
    [inlineSuggestions, isSimple, itemForRemoval, renderSuggestion]
  );

  return (
    <>
      {((isFocused === true && isSimple !== true) || forceShowBackdrop === true) &&
        typeof document !== 'undefined' &&
        createPortal(<div className={styles.backdrop} />, document?.body)}
      <Autosuggest
        ref={ref}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        getSectionSuggestions={getSectionSuggestions}
        shouldRenderSuggestions={shouldRenderSuggestions}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionHighlighted={onSuggestionHighlighted}
        renderInputComponent={renderInputComponent}
        multiSection
        theme={styles}
        inputProps={{
          placeholder: placeholder || defaultPlaceholder,
          value: inputValue,
          onChange,
          onFocus: onFocusInput,
          onBlur: onBlurInput,
          disabled: isDisabled,
          autoComplete: 'off',
          autoCapitalize: 'none',
          autoCorrect: 'off',
          autoFocus,
        }}
      />
    </>
  );
};

export const SearchField = TypedMemo(SearchFieldComponent);
